import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useTheme } from '@/context/ThemeContext';
import { FileText, ArrowLeft, ShieldCheck, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_BY_SLUG = {
  'privacy-policy':    { Icon: ShieldCheck, tint: 'emerald' },
  'data-disclaimer':   { Icon: AlertCircle, tint: 'amber' },
  'terms-of-service':  { Icon: FileText,    tint: 'sky' },
};

const TINT_STYLES = {
  emerald: { light: 'bg-emerald-50 text-emerald-600', dark: 'bg-emerald-500/20 text-emerald-300' },
  amber:   { light: 'bg-amber-50 text-amber-600',     dark: 'bg-amber-500/20 text-amber-300' },
  sky:     { light: 'bg-sky-50 text-sky-600',         dark: 'bg-sky-500/20 text-sky-300' },
};

/**
 * Shared renderer for CMS-backed legal pages
 * (Privacy Policy, Data Disclaimer, Terms of Service).
 *
 * Content is loaded from `/api/cms/pages/public/:slug` and rendered as
 * a title + intro + list of sections (each section is a title + HTML body).
 */
const LegalPage = ({ slug, canonical, fallbackTitle = 'Legal', fallbackDescription = '' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/api/cms/pages/public/${slug}`);
        if (!res.ok) throw new Error('Failed to load content');
        const data = await res.json();
        if (!cancelled) setContent(data?.content || {});
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchContent();
    return () => { cancelled = true; };
  }, [slug]);

  const iconMeta = ICON_BY_SLUG[slug] || ICON_BY_SLUG['terms-of-service'];
  const { Icon } = iconMeta;
  const tint = TINT_STYLES[iconMeta.tint] || TINT_STYLES.sky;

  const title = content?.title || fallbackTitle;
  const subtitle = content?.subtitle || '';
  const intro = content?.intro || '';
  const sections = Array.isArray(content?.sections) ? content.sections : [];
  const footerNote = content?.footer_note || '';
  const lastUpdated = content?.last_updated || '';
  const seoTitle = content?.seo?.title || `${title} | UniXperts`;
  const seoDescription = content?.seo?.description || fallbackDescription || subtitle;

  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-[#1a2830] text-white' : 'bg-slate-50 text-slate-900'}`}
      data-testid={`legal-page-${slug}`}
    >
      <SEO title={seoTitle} description={seoDescription} canonical={canonical || `/${slug}`} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-8 text-sm ${
            isDark ? 'text-white/70 hover:text-white' : 'text-slate-500 hover:text-slate-900'
          }`}
          data-testid="legal-back-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-full ${isDark ? tint.dark : tint.light}`}>
            <Icon className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="legal-page-title">
            {title}
          </h1>
        </div>

        {subtitle && (
          <p className={`mb-4 text-base ${isDark ? 'text-white/70' : 'text-slate-600'}`} data-testid="legal-page-subtitle">
            {subtitle}
          </p>
        )}

        {lastUpdated && (
          <p className={`text-sm mb-10 ${isDark ? 'text-white/50' : 'text-slate-500'}`} data-testid="legal-last-updated">
            Last updated: {lastUpdated}
          </p>
        )}

        {loading && (
          <div className={`animate-pulse ${isDark ? 'text-white/50' : 'text-slate-500'}`} data-testid="legal-loading">
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="p-4 rounded-lg bg-red-50 text-red-700 mb-6" data-testid="legal-error">
            We couldn't load this content right now. Please refresh in a minute.
          </div>
        )}

        {!loading && !error && (
          <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''} space-y-8 leading-relaxed`}>
            {intro && (
              <p className={`text-base ${isDark ? 'text-white/80' : 'text-slate-700'}`} data-testid="legal-intro">
                {intro}
              </p>
            )}

            {sections.map((sec, i) => (
              <section key={sec.id || i} data-testid={`legal-section-${sec.id || i}`}>
                <h2 className="text-xl font-bold mb-3">{sec.title}</h2>
                <div
                  className={`text-base ${isDark ? 'text-white/80' : 'text-slate-700'}`}
                  dangerouslySetInnerHTML={{ __html: sec.body_html || sec.body || '' }}
                />
              </section>
            ))}

            {footerNote && (
              <div
                className={`mt-10 p-4 rounded-lg border ${
                  isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white'
                } text-sm`}
                data-testid="legal-footer-note"
              >
                {footerNote}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalPage;
